import { c as _c } from "react/compiler-runtime";
import { getCustomDateFilterOptions } from "@components/common/faro-table/faro-table-filter/date-filter/date-filter-utils";
import { SphereAvatar } from "@components/header/sphere-avatar";
import DateAdapter from "@date-io/luxon";
import { ChipStatusText } from "@flat-ui";
import { AnnotationStatus } from "@faro-lotv/ielement-types";
import { sortAssigneeMembers } from "@pages/project-details/project-markups/markup-assignee-utils";
import { CURRENT_USER_SEARCH_TERM, ProjectMarkupsTableHeaders } from "@pages/project-details/project-markups/project-markups-table-utils";
import { MarkupStatusIcon } from "@pages/project-details/project-markups/status/markup-status-icon";
import { getMarkupStatusValue } from "@pages/project-details/project-markups/status/markup-status-utils";
import { markupsUniqueAssigneeSelector, markupsUniqueCreatorsSelector, markupsUniqueKeysSelector } from "@store/markups/markups-selector";
import { useAppSelector } from "@store/store-helper";
import { currentUserSelector } from "@store/user/user-selector";
import { getPrettyName, getUserInitials } from "@utils/user-utils";
import { useFilterQueryParams } from "@hooks/table-filters/use-filter-query-params";
import { useMemo } from "react";

/** Separates the active due date filter IDs in the key that keeps the filters memoized */
const DUE_DATE_FILTER_IDS_SEPARATOR = ",";

// Created once, so that every component using the filters shares the memoized result
const markupsLabelsSelector = markupsUniqueKeysSelector("labels");
const markupsAssigneesSelector = markupsUniqueAssigneeSelector();

/** Returns all the available filters for ProjectMarkup */
export function useProjectMarkupsFilterLists() {
  const $ = _c(22);
  const markupsLabels = useAppSelector(markupsLabelsSelector);
  const markupsAssignees = useAppSelector(markupsAssigneesSelector);
  const markupsCreators = useAppSelector(markupsUniqueCreatorsSelector);
  const currentUser = useAppSelector(currentUserSelector);
  const {
    getFilterIdsForColumn
  } = useFilterQueryParams();
  let t0;
  if ($[0] !== getFilterIdsForColumn) {
    t0 = getFilterIdsForColumn(ProjectMarkupsTableHeaders.dueDate).join(DUE_DATE_FILTER_IDS_SEPARATOR);
    $[0] = getFilterIdsForColumn;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const dueDateFilterIdsKey = t0;
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = getMarkupsStatusFilterOptions();
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== markupsLabels) {
    t2 = getMarkupsTagsFilterOptions(markupsLabels);
    $[3] = markupsLabels;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = getMarkupsAttachmentFilterOptions();
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== currentUser || $[7] !== markupsAssignees) {
    t4 = getMarkupsAssigneesFilterOptions(withMember(markupsAssignees, currentUser), currentUser?.identity);
    $[6] = currentUser;
    $[7] = markupsAssignees;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== currentUser || $[10] !== markupsCreators) {
    t5 = getMarkupsMemberFilterOptions(withMember(markupsCreators, currentUser), _temp, currentUser?.identity);
    $[9] = currentUser;
    $[10] = markupsCreators;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = getMarkupsDueDateFilterOptions();
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== dueDateFilterIdsKey) {
    t7 = getCustomDateFilterOptions(dueDateFilterIdsKey.split(DUE_DATE_FILTER_IDS_SEPARATOR), getMarkupDueTime);
    $[13] = dueDateFilterIdsKey;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t7) {
    t8 = {
      ...t6,
      ...t7
    };
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t2 || $[18] !== t4 || $[19] !== t5 || $[20] !== t8) {
    t9 = {
      [ProjectMarkupsTableHeaders.status]: t1,
      [ProjectMarkupsTableHeaders.tags]: t2,
      [ProjectMarkupsTableHeaders.attachments]: t3,
      [ProjectMarkupsTableHeaders.assignee]: t4,
      [ProjectMarkupsTableHeaders.creator]: t5,
      [ProjectMarkupsTableHeaders.dueDate]: t8
    };
    $[17] = t2;
    $[18] = t4;
    $[19] = t5;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const allFilters = t9;
  return allFilters;
}

/** Returns the members with the given one too, so the current user can always filter by themselves */
function _temp(markup) {
  return markup.createdBy;
}
function withMember(members, member) {
  return !member || members.some(({
    identity
  }) => identity === member.identity) ? members : [...members, member];
}
function getMarkupsStatusFilterOptions() {
  const noStatusFilter = {
    unclassified: {
      id: AnnotationStatus.Unclassified,
      hasEntityPassedFilter: markup => getMarkupStatusValue(markup.status?.value) === AnnotationStatus.Unclassified,
      label: ChipStatusText.unclassified,
      icon: <MarkupStatusIcon key={AnnotationStatus.Unclassified} status={AnnotationStatus.Unclassified} shouldHaveBorder={true} />
    }
  };
  const statusFilters = Object.values(AnnotationStatus).reduce((acc, value) => {
    return {
      ...acc,
      [value]: {
        id: value,
        hasEntityPassedFilter: markup => getMarkupStatusValue(markup.status?.value) === value,
        label: ChipStatusText[value],
        icon: <MarkupStatusIcon key={value} status={value} shouldHaveBorder={true} />
      }
    };
  }, {});
  return {
    ...noStatusFilter,
    ...statusFilters
  };
}

/** Returns all the filter options for markup tags */
function getMarkupsTagsFilterOptions(markupsLabels) {
  const untaggedFilter = {
    untagged: {
      id: "untagged",
      label: "Untagged",
      hasEntityPassedFilter: markup => !markup.labels || markup.labels.length === 0
    }
  };
  const tagFilters = markupsLabels.flat().reduce((acc, label) => {
    return {
      ...acc,
      [label.id]: {
        id: label.id,
        label: label.name,
        hasEntityPassedFilter: markup => {
          const tags = Array.isArray(markup.labels) ? markup.labels.map(label => label.id) : undefined;
          return tags?.includes(label.id) ?? false;
        }
      }
    };
  }, {});
  return {
    ...untaggedFilter,
    ...tagFilters
  };
}

/** Returns all the filter options for markup attachments */
function getMarkupsAttachmentFilterOptions() {
  return {
    hasAttachment: {
      id: "hasAttachment",
      hasEntityPassedFilter: markup => markup.attachmentsCount > 0,
      label: ""
    }
  };
}

/** Returns all the filter options for markup assignee */
function getMarkupsAssigneesFilterOptions(markupsAssignees, currentUserId) {
  const noAssigneeFilter = {
    unassigned: {
      id: "unassigned",
      label: "Unassigned",
      hasEntityPassedFilter: markup => markup.assignee?.values?.[0] === undefined
    }
  };
  const assigneeFilters = getMarkupsMemberFilterOptions(markupsAssignees, markup => markup.assignee?.values?.[0], currentUserId);
  return {
    ...noAssigneeFilter,
    ...assigneeFilters
  };
}

/** Returns a filter option for each member, matching the markups whose member returned by the callback is them */
function getMarkupsMemberFilterOptions(members, getMarkupMemberId, currentUserId) {
  return sortAssigneeMembers(members, currentUserId).reduce((acc, member) => {
    return {
      ...acc,
      [member.identity]: {
        id: member.identity,
        label: getPrettyName(member),
        searchTerms: [member.email, ...(currentUserId === member.identity ? [CURRENT_USER_SEARCH_TERM] : [])],
        hasEntityPassedFilter: markup => getMarkupMemberId(markup) === member.identity,
        icon: <SphereAvatar shouldUseSimplifiedAvatar={currentUserId !== member.identity} size={"small"} initials={getUserInitials(member) ?? ""} src={member.thumbnailUrl} />
      }
    };
  }, {});
}

/** Returns the time of the markup's due date, or undefined if it has none */
function getMarkupDueTime(markup) {
  return markup.dueDate?.value ? new DateAdapter().date(markup.dueDate.value)?.valueOf() : undefined;
}

/** Returns all the filter options for markup due date */
export function getMarkupsDueDateFilterOptions() {
  const dateAdapter = new DateAdapter();
  const today = dateAdapter.date();
  const startOfToday = dateAdapter.startOfDay(today);
  const endOfToday = dateAdapter.endOfDay(today);

  /* eslint-disable @typescript-eslint/no-magic-numbers -- these numbers are requited for giving duration */
  return {
    beforeToday: {
      id: "beforeToday",
      label: "Before today",
      hasEntityPassedFilter: markup => {
        const dueDate = markup.dueDate?.value ? dateAdapter.date(markup.dueDate.value)?.valueOf() : undefined;
        return dueDate !== undefined && dueDate < startOfToday.valueOf();
      }
    },
    today: {
      id: "today",
      label: "Today",
      hasEntityPassedFilter: markup => {
        const dueDate = markup.dueDate?.value ? dateAdapter.date(markup.dueDate.value)?.valueOf() : undefined;
        return dueDate !== undefined && dueDate >= startOfToday.valueOf() && dueDate < endOfToday.valueOf();
      }
    },
    tomorrow: {
      id: "tomorrow",
      label: "Tomorrow",
      hasEntityPassedFilter: markup => {
        const dueDate = markup.dueDate?.value ? dateAdapter.date(markup.dueDate.value)?.valueOf() : undefined;
        return dueDate !== undefined && dueDate >= dateAdapter.addDays(startOfToday, 1).valueOf() && dueDate < dateAdapter.addDays(startOfToday, 2).valueOf();
      }
    },
    next7Days: {
      id: "next7Days",
      label: "Next 7 days",
      hasEntityPassedFilter: markup => {
        const dueDate = markup.dueDate?.value ? dateAdapter.date(markup.dueDate.value)?.valueOf() : undefined;
        return dueDate !== undefined && dueDate >= dateAdapter.addDays(startOfToday, 0).valueOf() && dueDate < dateAdapter.addDays(startOfToday, 7).valueOf();
      }
    },
    next30Days: {
      id: "next30Days",
      label: "Next 30 days",
      hasEntityPassedFilter: markup => {
        const dueDate = markup.dueDate?.value ? dateAdapter.date(markup.dueDate.value)?.valueOf() : undefined;
        return dueDate !== undefined && dueDate >= dateAdapter.addDays(startOfToday, 0).valueOf() && dueDate < dateAdapter.addDays(startOfToday, 30).valueOf();
      }
    },
    next90Days: {
      id: "next90Days",
      label: "Next 90 days",
      hasEntityPassedFilter: markup => {
        const dueDate = markup.dueDate?.value ? dateAdapter.date(markup.dueDate.value)?.valueOf() : undefined;
        return dueDate !== undefined && dueDate >= dateAdapter.addDays(startOfToday, 0).valueOf() && dueDate < dateAdapter.addDays(startOfToday, 90).valueOf();
      }
    },
    noDate: {
      id: "noDate",
      label: "No date",
      hasEntityPassedFilter: markup => {
        const dueDate = markup.dueDate?.value;
        return dueDate === undefined;
      }
    }
  };
  /* eslint-enable @typescript-eslint/no-magic-numbers */
}